/**
 * Context pour gérer le mode dégradé de l'application
 * Permet une dégradation gracieuse en cas de problèmes
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { ClassifiedError, ErrorCategory, ErrorSeverity, classifyError, shouldEnterDegradedMode, generateErrorId } from '@/lib/errorClassification';
import { captureError } from '@/lib/sentry';
import { useToast } from '@/hooks/use-toast';

interface DegradedFeature {
  featureId: string;
  reason: string;
  fallbackAvailable: boolean;
  disabledAt: Date;
}

interface ErrorRecord {
  id: string;
  error: ClassifiedError;
  timestamp: Date;
  resolved: boolean;
}

interface DegradedModeState {
  isInDegradedMode: boolean;
  degradedFeatures: DegradedFeature[];
  recentErrors: ErrorRecord[];
  networkStatus: 'online' | 'offline' | 'unstable';
}

interface DegradedModeContextValue extends DegradedModeState {
  reportError: (error: Error | unknown, featureId?: string) => ClassifiedError;
  disableFeature: (featureId: string, reason: string, fallbackAvailable?: boolean) => void;
  enableFeature: (featureId: string) => void;
  isFeatureDisabled: (featureId: string) => boolean;
  clearErrors: () => void;
  exitDegradedMode: () => void;
}

const DegradedModeContext = createContext<DegradedModeContextValue | null>(null);

const MAX_ERRORS_HISTORY = 50;
const DEGRADED_MODE_THRESHOLD = 3; // Nombre d'erreurs avant mode dégradé

export function DegradedModeProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [state, setState] = useState<DegradedModeState>({
    isInDegradedMode: false,
    degradedFeatures: [],
    recentErrors: [],
    networkStatus: 'online'
  });

  // Monitoring de la connexion réseau
  useEffect(() => {
    const handleOnline = () => {
      setState(s => ({ ...s, networkStatus: 'online' }));
      toast({
        title: 'Connexion rétablie',
        description: 'Votre connexion internet est de nouveau active.',
        variant: 'default'
      });
    };

    const handleOffline = () => {
      setState(s => ({ 
        ...s, 
        networkStatus: 'offline',
        isInDegradedMode: true 
      }));
      toast({
        title: 'Mode hors ligne',
        description: 'Certaines fonctionnalités sont indisponibles.',
        variant: 'destructive'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // État initial
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  // Auto-sortie du mode dégradé après stabilisation
  useEffect(() => {
    if (!state.isInDegradedMode) return;

    const recentUnresolvedErrors = state.recentErrors.filter(
      e => !e.resolved && 
      (Date.now() - e.timestamp.getTime()) < 60000 // 1 minute
    );

    if (recentUnresolvedErrors.length === 0 && state.networkStatus === 'online') {
      const timeout = setTimeout(() => {
        setState(s => ({ ...s, isInDegradedMode: false }));
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [state.recentErrors, state.isInDegradedMode, state.networkStatus]);

  const reportError = useCallback((error: Error | unknown, featureId?: string): ClassifiedError => {
    const classified = classifyError(error);
    const errorId = generateErrorId();
    
    // Log vers Sentry
    captureError(classified.original, {
      errorId,
      category: classified.category,
      severity: classified.severity,
      featureId
    });

    // Enregistrer l'erreur
    setState(s => {
      const newErrors = [
        { id: errorId, error: classified, timestamp: new Date(), resolved: false },
        ...s.recentErrors.slice(0, MAX_ERRORS_HISTORY - 1)
      ];

      // Compter les erreurs récentes non résolues
      const recentUnresolved = newErrors.filter(
        e => !e.resolved && 
        (Date.now() - e.timestamp.getTime()) < 60000
      ).length;

      // Entrer en mode dégradé si nécessaire
      const shouldDegrade = shouldEnterDegradedMode(classified) || 
        recentUnresolved >= DEGRADED_MODE_THRESHOLD;

      return {
        ...s,
        recentErrors: newErrors,
        isInDegradedMode: s.isInDegradedMode || shouldDegrade
      };
    });

    return classified;
  }, []);

  const disableFeature = useCallback((featureId: string, reason: string, fallbackAvailable = false) => {
    setState(s => {
      if (s.degradedFeatures.some(f => f.featureId === featureId)) {
        return s;
      }
      return {
        ...s,
        degradedFeatures: [
          ...s.degradedFeatures,
          { featureId, reason, fallbackAvailable, disabledAt: new Date() }
        ],
        isInDegradedMode: true
      };
    });
  }, []);

  const enableFeature = useCallback((featureId: string) => {
    setState(s => ({
      ...s,
      degradedFeatures: s.degradedFeatures.filter(f => f.featureId !== featureId)
    }));
  }, []);

  const isFeatureDisabled = useCallback((featureId: string): boolean => {
    return state.degradedFeatures.some(f => f.featureId === featureId);
  }, [state.degradedFeatures]);

  const clearErrors = useCallback(() => {
    setState(s => ({
      ...s,
      recentErrors: s.recentErrors.map(e => ({ ...e, resolved: true }))
    }));
  }, []);

  const exitDegradedMode = useCallback(() => {
    setState(s => ({
      ...s,
      isInDegradedMode: false,
      degradedFeatures: []
    }));
  }, []);

  return (
    <DegradedModeContext.Provider
      value={{
        ...state,
        reportError,
        disableFeature,
        enableFeature,
        isFeatureDisabled,
        clearErrors,
        exitDegradedMode
      }}
    >
      {children}
    </DegradedModeContext.Provider>
  );
}

export function useDegradedMode() {
  const context = useContext(DegradedModeContext);
  if (!context) {
    throw new Error('useDegradedMode must be used within DegradedModeProvider');
  }
  return context;
}
